import React, { useState, useEffect } from "react";
import styles from "./index.module.css";
import ReportTableADA from "Reporting/Components/ReportTableADA";
import { useADAGet } from "./useADAGet";
import Loader from "shared/Loader";

/*
 * Table displaying different items in a report using Diamond integration
 */
export default function ReportTableListADA({
  filters,
  setReportsLength,
  setReportData,
  fetchTrigger,
}) {
  const [reports, setReports] = useState([]);
  const [selectedColumnsByReport, setSelectedColumnsByReport] = useState({});
  const [sortedAdaData, setSortedAdaData] = useState([]);

  const { data, isLoading, isError, refetch } = useADAGet({
    events: filters.events,
  });

  useEffect(() => {
    if (typeof fetchTrigger !== "undefined") {
      refetch?.();
    }
  }, [fetchTrigger]);

  useEffect(() => {
    if (!Array.isArray(data)) return;

    const reportsChanged =
      reports.length !== data.length ||
      reports.some((r, i) => r.reportTitle !== data[i].reportTitle);

    if (reportsChanged) {
      setReports(data);
    }

    const newSorted = {};
    data.forEach((report) => {
      const key = report.reportTitle;
      newSorted[key] = report.adaData || [];
    });
    setSortedAdaData(newSorted);

    setReportData?.((prev) => {
      const parentChanged =
        !prev ||
        prev.length !== data.length ||
        prev.some((r, i) => r.reportTitle !== data[i].reportTitle);
      return parentChanged ? data : prev;
    });

    setReportsLength?.(data.length || 0);
  }, [data]);

  useEffect(() => {
    if (reports.length === 0) return;

    const reportsWithSorted = reports.map((report) => ({
      ...report,
      sortedAdaData: sortedAdaData[report.reportTitle],
    }));

    setReportData?.(reportsWithSorted);
  }, [sortedAdaData, reports, setReportData]);

  const handleSortedReportsChange = (title, rows, selectedCols) => {
    if (!rows || !selectedCols) return;

    const sanitizedRows = rows.map((row) => ({
      ...row,
      columns: row.columns.filter((c) => selectedCols.includes(c.columnName)),
    }));

    const visibleRows = sanitizedRows.filter((r) => r.columns.length > 0);

    setSortedAdaData((prev) => ({ ...prev, [title]: visibleRows }));

    setReportData((prevReports) => {
      if (!prevReports) return [];
      return prevReports.map((r) =>
        r.reportTitle === title ? { ...r, sortedAdaData: visibleRows } : r
      );
    });
  };

  if (isError) {
    return <div>Something went wrong...</div>;
  }
  if (isLoading) {
    return <Loader />;
  }
  if (reports.length === 0) {
    return <div>No Reports Found</div>;
  }

  return (
    <div className={styles.container}>
      {reports.map((report, index) => (
        <React.Fragment key={index}>
          <ReportTableADA
            report={report}
            filters={filters}
            selectedColumns={selectedColumnsByReport[report.reportTitle] || []}
            onSelectedColumnsChange={(cols) =>
              setSelectedColumnsByReport((prev) => ({
                ...prev,
                [report.reportTitle]: cols,
              }))
            }
            onSortedReportsChange={(sortedRows, selectedCols) =>
              handleSortedReportsChange(
                report.reportTitle,
                sortedRows,
                selectedCols ||
                  selectedColumnsByReport[report.reportTitle] ||
                  []
              )
            }
          />
        </React.Fragment>
      ))}
    </div>
  );
}
